/**
 * SkyCubemapPass.ts — bakes a sky+sun cubemap (6-layer rgba16float).
 *
 * One physical texture, two views:
 *   - storageView : 2d-array view (6 layers) used by the bake compute shader
 *   - cubeView    : cube view sampled by the water render pass
 *
 * Re-runs when sun direction changes (LightingUniforms checks delta), or
 * unconditionally on the first frame.
 */

import wgsl from "../shaders/skybake.wgsl?raw";
import type { LightingUniforms } from "../rendering/LightingUniforms";
import type { PaletteUniforms } from "../rendering/PaletteUniforms";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import type { TerrainRenderPass } from "./TerrainRenderPass";

export interface SkyCubemapPassOptions {
  /** Square face resolution. Default 256. */
  resolution?: number;
  /** Re-bake period in frames while moving camera. Default 8. */
  reBakeEveryNFrames?: number;
  /** Camera movement threshold in metres triggering re-bake. Default 4. */
  moveThresholdM?: number;
}

export class SkyCubemapPass implements RenderPass {
  readonly name = "SkyCubemapPass";

  readonly resolution: number;
  readonly reBakeEveryNFrames: number;
  readonly moveThresholdM: number;

  private device: GPUDevice;
  private lighting: LightingUniforms;

  private texture: GPUTexture;
  private storageView: GPUTextureView;
  readonly cubeView: GPUTextureView;
  private params: GPUBuffer;

  private pipeline: GPUComputePipeline;
  private bindGroup: GPUBindGroup;

  private dirty = true;
  private lastSunX = NaN;
  private lastSunY = NaN;
  private lastSunZ = NaN;
  private lastCaptureX = NaN;
  private lastCaptureZ = NaN;
  private frameCounter = 0;
  private terrainExtent: number;
  private terrainResolution: number;

  constructor(
    device: GPUDevice,
    lighting: LightingUniforms,
    palette: PaletteUniforms,
    terrainPass: TerrainRenderPass,
    opts: SkyCubemapPassOptions = {},
  ) {
    this.device = device;
    this.lighting = lighting;
    this.resolution = opts.resolution ?? 256;
    this.reBakeEveryNFrames = opts.reBakeEveryNFrames ?? 8;
    this.moveThresholdM = opts.moveThresholdM ?? 4;
    this.terrainExtent = terrainPass.heightmap.extent;
    this.terrainResolution = terrainPass.heightmap.resolution;

    this.texture = device.createTexture({
      label: "SkyCubemapPass.tex",
      size: {
        width: this.resolution,
        height: this.resolution,
        depthOrArrayLayers: 6,
      },
      format: "rgba16float",
      usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.storageView = this.texture.createView({
      label: "SkyCubemapPass.storage",
      dimension: "2d-array",
      arrayLayerCount: 6,
    });
    this.cubeView = this.texture.createView({
      label: "SkyCubemapPass.cube",
      dimension: "cube",
      arrayLayerCount: 6,
    });

    this.params = device.createBuffer({
      label: "SkyCubemapPass.params",
      size: 32,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const module = device.createShaderModule({
      label: "skybake.wgsl",
      code: wgsl,
    });
    const bgl = device.createBindGroupLayout({
      label: "SkyCubemapPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: "uniform" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: "uniform" },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 4,
          visibility: GPUShaderStage.COMPUTE,
          storageTexture: {
            access: "write-only",
            format: "rgba16float",
            viewDimension: "2d-array",
          },
        },
      ],
    });
    this.pipeline = device.createComputePipeline({
      label: "SkyCubemapPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [bgl] }),
      compute: { module, entryPoint: "cs_main" },
    });
    this.bindGroup = device.createBindGroup({
      label: "SkyCubemapPass.bg",
      layout: bgl,
      entries: [
        { binding: 0, resource: { buffer: lighting.buffer } },
        { binding: 1, resource: { buffer: palette.buffer } },
        { binding: 2, resource: { buffer: this.params } },
        { binding: 3, resource: terrainPass.heightmapView() },
        { binding: 4, resource: this.storageView },
      ],
    });
  }

  /** Force a re-bake on next encode. */
  invalidate(): void {
    this.dirty = true;
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    // Detect sun direction change cheaply via azimuth/elevation.
    const az = this.lighting.state.sunAzimuth;
    const el = this.lighting.state.sunElevation;
    const ce = Math.cos(el);
    const sx = ce * Math.sin(az);
    const sz = ce * -Math.cos(az);
    const sy = Math.sin(el);
    if (sx !== this.lastSunX || sy !== this.lastSunY || sz !== this.lastSunZ) {
      this.dirty = true;
      this.lastSunX = sx;
      this.lastSunY = sy;
      this.lastSunZ = sz;
    }

    this.frameCounter++;
    const capX = frame.camera.position[0]!;
    const capZ = frame.camera.position[2]!;
    const moved =
      Number.isNaN(this.lastCaptureX) ||
      Number.isNaN(this.lastCaptureZ) ||
      Math.hypot(capX - this.lastCaptureX, capZ - this.lastCaptureZ) >=
        this.moveThresholdM;
    const periodic = this.frameCounter % this.reBakeEveryNFrames === 0;
    if (moved || periodic) this.dirty = true;

    if (!this.dirty) return;
    this.dirty = false;
    this.lastCaptureX = capX;
    this.lastCaptureZ = capZ;

    const params = new Float32Array(8);
    params[0] = this.terrainExtent;
    params[1] = this.terrainResolution;
    params[2] = capX;
    params[3] = capZ;
    params[4] = frame.state.waterLevel;
    params[5] = 1200.0; // max ray length (m)
    params[6] = 10.0; // step length (m)
    params[7] = 0.55; // shoreline blend strength
    this.device.queue.writeBuffer(this.params, 0, params);

    const pass = encoder.beginComputePass({ label: "SkyCubemapPass" });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    const wg = Math.ceil(this.resolution / 8);
    pass.dispatchWorkgroups(wg, wg, 6);
    pass.end();
  }

  destroy(): void {
    this.params.destroy();
    this.texture.destroy();
  }
}
